import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
  Shield, Star, Users, Clock, CheckCircle, ArrowRight, ChevronLeft, ChevronRight
} from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const HERO_IMG = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=900";

const TREATMENT_ICONS = ["✂️", "🦷", "💉", "🏃", "🩺", "✈️"];

function TrustStrip() {
  const { t } = useTranslation();
  const items = t("trust.items", { returnObjects: true }) || [];
  return (
    <div className="trust-strip py-8">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
          <p className="text-white/70 text-sm font-medium uppercase tracking-widest hidden md:block">{t("trust.title")}</p>
          {items.map((item, i) => (
            <div key={i} className="text-center" data-testid={`trust-stat-${i}`}>
              <div className="text-2xl md:text-3xl font-bold text-white font-outfit">{item.value}</div>
              <div className="text-white/65 text-xs mt-0.5">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorksSection() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const steps = t("howItWorks.steps", { returnObjects: true }) || [];
  const icons = [<Users className="w-6 h-6" />, <Shield className="w-6 h-6" />, <Star className="w-6 h-6" />, <CheckCircle className="w-6 h-6" />];

  return (
    <section className="py-20 md:py-24 bg-white" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className={`text-center mb-14 anim-init ${isVisible ? "in-view" : ""}`}>
          <span className="overline-text">{t("howItWorks.overline")}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-4">
            {t("howItWorks.title")}
          </h2>
          <p className="text-[#6B7280] max-w-xl mx-auto">{t("howItWorks.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`anim-init stagger-${i + 1} ${isVisible ? "in-view" : ""} bg-[#F8FAFC] rounded-3xl p-7 border border-gray-100 treatment-card group cursor-default`}
              data-testid={`how-it-works-step-${i + 1}`}
            >
              <div className="w-12 h-12 rounded-2xl why-icon flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform">
                {icons[i]}
              </div>
              <div className="text-4xl font-black text-gray-100 font-outfit mb-2">{step.step}</div>
              <h3 className="text-lg font-bold text-[#0B3C5D] font-outfit mb-2">{step.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TreatmentsSection() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const items = t("treatments.items", { returnObjects: true }) || [];

  return (
    <section className="py-20 md:py-24 bg-[#F8FAFC]" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className={`flex flex-col md:flex-row justify-between items-start md:items-end mb-12 anim-init ${isVisible ? "in-view" : ""}`}>
          <div>
            <span className="overline-text">{t("treatments.overline")}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2">
              {t("treatments.title")}
            </h2>
          </div>
          <Link
            to="/treatments"
            className="mt-4 md:mt-0 flex items-center gap-2 text-sm font-semibold text-[#3FA7D6] hover:gap-3 transition-all"
            data-testid="view-all-treatments"
          >
            {t("treatments.viewAll")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <div
              key={i}
              className={`anim-init stagger-${(i % 6) + 1} ${isVisible ? "in-view" : ""} bg-white rounded-3xl overflow-hidden border border-gray-100 treatment-card group`}
              data-testid={`treatment-card-${i}`}
            >
              <div className={`h-3 w-full bg-gradient-to-r ${item.color}`} />
              <div className="p-7">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit">{item.title}</h3>
                  <span className="text-xs font-semibold px-3 py-1 bg-[#3FA7D6]/12 text-[#3FA7D6] rounded-full whitespace-nowrap ml-2">
                    {item.saving}
                  </span>
                </div>
                <p className="text-sm text-[#6B7280] leading-relaxed mb-6">{item.desc}</p>
                <Link
                  to="/contact"
                  className="text-sm font-semibold text-[#0B3C5D] flex items-center gap-1 group-hover:text-[#3FA7D6] transition-colors"
                >
                  {t("treatments.requestQuote")} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const cities = t("destinations.cities", { returnObjects: true }) || [];

  return (
    <section className="py-20 md:py-24 bg-white overflow-hidden" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className={`mb-10 anim-init ${isVisible ? "in-view" : ""}`}>
          <span className="overline-text">{t("destinations.overline")}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-3">
            {t("destinations.title")}
          </h2>
          <p className="text-[#6B7280] max-w-lg">{t("destinations.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-6 md:px-[calc((100vw-1200px)/2+24px)] pb-4">
        {cities.map((city, i) => (
          <div
            key={i}
            className={`flex-shrink-0 w-72 snap-start rounded-3xl overflow-hidden relative dest-card cursor-pointer anim-init stagger-${i + 1} ${isVisible ? "in-view" : ""}`}
            data-testid={`destination-card-${i}`}
          >
            <img
              src={city.image}
              alt={city.name}
              className="w-full h-80 object-cover transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B3C5D]/85 via-[#0B3C5D]/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-2xl font-bold text-white font-outfit mb-1">{city.name}</h3>
              <p className="text-white/75 text-sm mb-3">{city.desc}</p>
              <span className="inline-block px-3 py-1 bg-[#3FA7D6]/30 backdrop-blur-sm border border-[#3FA7D6]/30 text-white text-xs font-semibold rounded-full">
                {city.saving}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhyChooseSection() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const items = t("whyChoose.items", { returnObjects: true }) || [];
  const icons = [
    <Users className="w-5 h-5" />,
    <Shield className="w-5 h-5" />,
    <CheckCircle className="w-5 h-5" />,
    <Clock className="w-5 h-5" />,
    <Star className="w-5 h-5" />,
    <ArrowRight className="w-5 h-5" />
  ];

  return (
    <section className="py-20 md:py-24 bg-[#F8FAFC]" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className={`anim-init ${isVisible ? "in-view" : ""}`}>
            <span className="overline-text">{t("whyChoose.overline")}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-4">
              {t("whyChoose.title")}
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-8">{t("whyChoose.subtitle")}</p>
            <Link to="/contact" className="btn-primary inline-flex" data-testid="why-choose-cta">
              {t("nav.getConsultation")}
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((item, i) => (
              <div
                key={i}
                className={`anim-init stagger-${(i % 4) + 1} ${isVisible ? "in-view" : ""} bg-white rounded-2xl p-6 border border-gray-100 treatment-card`}
                data-testid={`why-choose-item-${i}`}
              >
                <div className="w-9 h-9 rounded-xl why-icon flex items-center justify-center text-white mb-4">
                  {icons[i]}
                </div>
                <h4 className="font-bold text-[#0B3C5D] text-sm mb-1.5">{item.title}</h4>
                <p className="text-xs text-[#6B7280] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [currentIndex, setCurrentIndex] = useState(0);
  const items = t("testimonials.items", { returnObjects: true }) || [];

  useEffect(() => {
    if (!emblaApi) return;
    const auto = setInterval(() => emblaApi.scrollNext(), 5000);
    emblaApi.on("select", () => setCurrentIndex(emblaApi.selectedScrollSnap()));
    return () => clearInterval(auto);
  }, [emblaApi]);

  return (
    <section className="py-20 md:py-24 bg-white" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className={`text-center mb-12 anim-init ${isVisible ? "in-view" : ""}`}>
          <span className="overline-text">{t("testimonials.overline")}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-4">
            {t("testimonials.title")}
          </h2>
          <p className="text-[#6B7280]">{t("testimonials.subtitle")}</p>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-5">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)] bg-[#F8FAFC] rounded-3xl p-8 border border-gray-100"
                data-testid={`testimonial-card-${i}`}
              >
                <div className="flex gap-0.5 mb-4 star-rating">
                  {[...Array(5)].map((_, si) => <Star key={si} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-[#374151] leading-relaxed mb-6 text-sm italic">"{item.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3FA7D6] to-[#0B3C5D] flex items-center justify-center text-white font-bold text-sm">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B3C5D] text-sm">{item.name}</div>
                    <div className="text-xs text-[#6B7280]">{item.location} · {item.treatment}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-6">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              className={`rounded-full transition-all ${i === currentIndex ? "w-6 h-2 bg-[#3FA7D6]" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CTABanner() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section className="cta-gradient py-20" ref={ref}>
      <div className={`max-w-[1200px] mx-auto px-6 text-center anim-init ${isVisible ? "in-view" : ""}`}>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-outfit tracking-tight mb-4">
          {t("ctaBanner.title")}
        </h2>
        <p className="text-white/80 text-lg max-w-xl mx-auto mb-10">{t("ctaBanner.subtitle")}</p>
        <Link
          to="/contact"
          data-testid="cta-banner-button"
          className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B3C5D] font-bold rounded-full hover:bg-[#F8FAFC] transition-all duration-200 hover:scale-105 hover:shadow-2xl text-base"
        >
          {t("ctaBanner.button")} <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const stats = [
    { val: t("hero.stat1"), label: t("hero.stat1Label") },
    { val: t("hero.stat2"), label: t("hero.stat2Label") },
    { val: t("hero.stat3"), label: t("hero.stat3Label") },
  ];

  return (
    <div>
      <Helmet>
        <title>Affordable Medical Tourism in Turkey | Hair Transplant & Dental Care | Monimek</title>
        <meta name="description" content="Save up to 70% on medical treatments in JCI-accredited hospitals in Turkey. Get a free consultation for hair transplants, dental implants, and cosmetic surgery." />
        <link rel="canonical" href="https://www.monizmek.com/" />
      </Helmet>
      {/* HERO */}
      <section className="min-h-screen flex items-center bg-[#F8FAFC] pt-20 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-0 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
          {/* Left */}
          <div className="hero-left">
            <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-[#3FA7D6]/12 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#3FA7D6] pulse-dot" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#3FA7D6]">{t("hero.badge")}</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-black text-[#0B3C5D] font-outfit tracking-tight leading-none mb-3">
              {t("hero.headline1")}
            </h1>
            <h1 className="text-5xl sm:text-6xl font-black text-[#3FA7D6] font-outfit tracking-tight leading-none mb-6">
              {t("hero.headline2")}
            </h1>
            <p className="text-lg text-[#6B7280] leading-relaxed max-w-lg mb-10">
              {t("hero.subtext")}
            </p>
            <div className="flex gap-4 flex-wrap mb-12">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                {t("hero.cta1")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/treatments" data-testid="hero-cta-secondary" className="btn-outline">
                {t("hero.cta2")}
              </Link>
            </div>
            <div className="flex gap-10 pt-8 border-t border-gray-200">
              {stats.map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <div className="text-2xl sm:text-3xl font-black text-[#0B3C5D] font-outfit">{s.val}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Image */}
          <div className="hero-right relative hidden sm:block">
            <div className="rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(11,60,93,0.15)]" style={{ aspectRatio: "4/5", maxHeight: 600 }}>
              <img
                src={HERO_IMG}
                alt="Medical consultation"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating Glass Card */}
            <div
              className="absolute -bottom-4 -left-6 glass-card rounded-2xl p-5 shadow-xl max-w-[210px]"
              data-testid="hero-float-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-xl bg-[#3FA7D6]/20 flex items-center justify-center">
                  <Shield className="w-3.5 h-3.5 text-[#3FA7D6]" />
                </div>
                <span className="text-xs font-bold text-[#0B3C5D]">{t("hero.floatTitle")}</span>
              </div>
              <div className="text-2xl font-black text-[#0B3C5D] font-outfit">{t("hero.floatSave")}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">{t("hero.floatSub")}</div>
              <div className="flex items-center gap-1.5 mt-3">
                <span className="w-2 h-2 rounded-full bg-green-400 pulse-dot" />
                <span className="text-xs text-green-600 font-semibold">{t("hero.floatVerified")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />
      <HowItWorksSection />
      <TreatmentsSection />
      <DestinationsSection />
      <WhyChooseSection />
      <TestimonialsSection />
      <CTABanner />
    </div>
  );
}
